import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Lock, Eye, EyeOff, Shield, Sparkles, ChevronDown } from 'lucide-react';
import SEO from '@/components/SEO';

const DEMO_PROFILES = [
  {
    id: 'mercer',
    label: 'Financial Planning Firm',
    description: 'Mercer & Partners — classic advisory, lead-driven, 5 clients, 3 leads.',
    email: 'owner@mercerfp.com',
  },
  {
    id: 'altitude',
    label: 'Multi-Family Office',
    description: 'Altitude FO — 7 client families, full service lines, 4 active leads.',
    email: 'owner@altitudefo.com',
  },
  {
    id: 'kingston',
    label: 'Single Family Office',
    description: 'Kingston Family Trust — one family, 6 entities, Leads tab disabled.',
    email: 'owner@kingstonfo.com',
  },
  {
    id: 'redwood',
    label: 'Boutique Wealth',
    description: 'Redwood Wealth — 6 clients, ESG tilt, active pipeline.',
    email: 'owner@redwoodwealth.com',
  },
];
const DEMO_PW = 'DemoQ2026!';

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, loginWithMicrosoft, verify2FA } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);

  // Demo Mode — internal-only panel. Hidden in production, visible only when ?dev=1 is in the URL.
  const [showDemo, setShowDemo] = useState(false);
  const showDevDemoPanel = (typeof window !== 'undefined') && new URLSearchParams(window.location.search).get('dev') === '1';

  // Deep-link from pitch room: /login?demo=owner@altitudefo.com
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const demo = searchParams.get('demo');
    if (!demo) return;
    const profile = DEMO_PROFILES.find((p) => p.email === demo);
    if (profile) {
      setEmail(profile.email);
      setPassword(DEMO_PW);
      toast.success(`Loaded ${profile.label} demo — hit "Sign in"`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickDemo = (profile) => {
    setEmail(profile.email);
    setPassword(DEMO_PW);
    setShowDemo(false);
    toast.success(`Loaded ${profile.label} — hit "Sign in" to explore`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password, rememberDevice);
    
    if (result.requires_2fa) {
      // User has 2FA enabled - show 2FA input
      setRequires2FA(true);
      setTempToken(result.temp_token);
      toast.info('Please enter your 2FA code');
    } else if (result.success) {
      toast.success('Login successful');
      navigate('/app');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error('Please enter a valid 6-digit code or recovery code');
      return;
    }
    
    setVerifying2FA(true);
    
    const result = await verify2FA(twoFactorCode, tempToken);
    
    if (result.success) {
      toast.success('Login successful');
      if (result.warning) {
        toast.warning(result.warning);
      }
      navigate('/app');
    } else {
      toast.error(result.error || 'Invalid 2FA code');
    }
    
    setVerifying2FA(false);
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  const handleMicrosoftLogin = () => {
    loginWithMicrosoft();
  };

  const handleBackTo2FA = () => {
    setRequires2FA(false);
    setTempToken(null);
    setTwoFactorCode('');
  };

  // 2FA Verification View
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-[#F9F7F5] flex" data-testid="2fa-page">
        <div className="w-full lg:w-1/2 flex flex-col px-8 py-6">
          <button 
            onClick={handleBackTo2FA}
            className="caffeine-btn-ghost inline-flex items-center gap-2 w-fit mb-8"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>

          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-md">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 bg-[#4A3B32] rounded-md flex items-center justify-center">
                  <span className="text-white font-bold">Q1</span>
                </div>
                <span className="font-heading text-2xl text-[#2C2420]">QIndex One</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#6B8E6B]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#6B8E6B]" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl text-[#2C2420]">
                    Two-Factor Authentication
                  </h1>
                  <p className="text-[#5D524D] text-sm">
                    Enter the code from your authenticator app
                  </p>
                </div>
              </div>

              <form onSubmit={handle2FASubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="2fa-code" className="text-[#2C2420]">Verification Code</Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^A-Za-z0-9-]/g, ''))}
                    placeholder="000000 or XXXX-XXXX"
                    className="caffeine-input text-center text-2xl tracking-widest"
                    maxLength={9}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    data-testid="2fa-code-input"
                  />
                  <p className="text-xs text-[#8C8078] text-center">
                    Enter 6-digit code or recovery code (XXXX-XXXX)
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="caffeine-btn-primary w-full"
                  disabled={verifying2FA}
                  data-testid="verify-2fa-btn"
                >
                  {verifying2FA ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Verify & Sign In
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 p-4 bg-[#4A3B32]/5 rounded-lg">
                <p className="text-xs text-[#5D524D]">
                  <strong>Lost access to your authenticator?</strong><br />
                  Use one of your recovery codes to sign in.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#8C8078] text-center">
            Fictional demo environment. All data is synthetic.
          </p>
        </div>

        <div className="hidden lg:flex w-1/2 bg-[#6B8E6B] items-center justify-center p-12">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Shield className="w-10 h-10 text-white/80" />
            </div>
            <h2 className="font-heading text-3xl text-white mb-4">
              Enhanced Security
            </h2>
            <p className="text-white/70">
              Two-factor authentication adds an extra layer of protection to your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login View
  return (
    <div className="min-h-screen bg-[#F9F7F5] flex" data-testid="login-page">
      <SEO page="login" noindex={false} />
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col px-8 py-6">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/')}
          className="caffeine-btn-ghost inline-flex items-center gap-2 w-fit mb-8"
          data-testid="back-to-landing-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-[#4A3B32] rounded-md flex items-center justify-center">
                <span className="text-white font-bold">Q1</span>
              </div>
              <span className="font-heading text-2xl text-[#2C2420]">QIndex One</span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl text-[#2C2420] mb-2">
              Sign in to Client 360
            </h1>
            <p className="text-[#5D524D] mb-6">
              Use your work account or test credentials
            </p>

            {/* Demo Mode — DEV-ONLY panel (visible with ?dev=1). Public prospects use /demo-access/:slug instead. */}
            {showDevDemoPanel && (
            <div className="mb-6 border border-[#A86A3A]/30 bg-[#FAF5F0] rounded-lg" data-testid="demo-mode-panel">
              <button
                type="button"
                onClick={() => setShowDemo(v => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
                data-testid="demo-mode-toggle"
              >
                <span className="flex items-center gap-2 text-sm text-[#2C2420]">
                  <Sparkles className="w-4 h-4 text-[#A86A3A]" />
                  <strong>Demo Mode</strong>
                  <span className="text-xs text-[#8C8078]">— explore 5 pre-seeded firm profiles</span>
                </span>
                <ChevronDown className={`w-4 h-4 text-[#8C8078] transition-transform ${showDemo ? 'rotate-180' : ''}`} />
              </button>
              {showDemo && (
                <div className="border-t border-[#A86A3A]/20 px-4 py-3 space-y-2" data-testid="demo-mode-profiles">
                  {DEMO_PROFILES.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => pickDemo(p)}
                      className="w-full text-left p-2.5 rounded-md hover:bg-white border border-transparent hover:border-[#A86A3A]/30 transition-colors"
                      data-testid={`demo-profile-${p.id}`}
                    >
                      <p className="text-sm font-medium text-[#2C2420]">{p.label}</p>
                      <p className="text-xs text-[#6B5B52] leading-snug mt-0.5">{p.description}</p>
                    </button>
                  ))}
                  <p className="text-[10px] text-[#8C8078] pt-1 border-t border-[#A86A3A]/20 mt-1">
                    Each profile auto-fills the owner email. Password is shared — hit "Sign in" to explore.
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Social Sign-In Buttons */}
            <div className="space-y-3 mb-6">
              {/* Google Sign-In Button */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full bg-white hover:bg-gray-50 text-[#2C2420] border border-[#E6E0D9] shadow-sm"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              {/* Microsoft Sign-In Button — hidden until REACT_APP_MS_CLIENT_ID is set */}
              {process.env.REACT_APP_MS_CLIENT_ID && (
                <Button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  className="w-full bg-white hover:bg-gray-50 text-[#2C2420] border border-[#E6E0D9] shadow-sm"
                  data-testid="microsoft-login-btn"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  Continue with Microsoft
                </Button>
              )}
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E6E0D9]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#F9F7F5] text-[#8C8078]">or sign in with credentials</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2C2420]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="caffeine-input"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#2C2420]">Password</Label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs text-[#4A3B32] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="caffeine-input pr-10"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8078] hover:text-[#4A3B32]"
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember This Device */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-device"
                  checked={rememberDevice}
                  onCheckedChange={setRememberDevice}
                  className="border-[#D4CEC8] data-[state=checked]:bg-[#4A3B32] data-[state=checked]:border-[#4A3B32]"
                  data-testid="remember-device-checkbox"
                />
                <label
                  htmlFor="remember-device"
                  className="text-sm text-[#5D524D] cursor-pointer select-none"
                >
                  Remember this device for 30 days
                </label>
              </div>

              <Button 
                type="submit" 
                className="caffeine-btn-primary w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-xs text-[#8C8078] text-center">
          QIndex One — Client Information Operating Layer
        </p>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex w-1/2 bg-[#4A3B32] items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Lock className="w-10 h-10 text-white/80" />
          </div>
          <h2 className="font-heading text-3xl text-white mb-4">
            Secure Client Intelligence
          </h2>
          <p className="text-white/70">
            QIndex One is an operating layer — it organises, permission-controls, and verifies what already exists in your stack.
          </p>
          <div className="mt-8 pt-8 border-t border-white/10">
            <p className="text-sm text-white/50">
              Documents stored securely. Links to source systems indexed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
